import React, { useCallback, useMemo } from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import * as Yup from 'yup';
import { Theme } from '@app/theme';
import { Formik, Form, FormikHelpers } from 'formik';
import { Input, FormikError } from '@app/form';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Button, SubmitButton, Captcha } from '@app/core';
import { useLogin, Account } from 'queries';

const useStyles = createUseStyles((theme: Theme) => ({
  buttonsLine: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.marginBase * 2,
  },
  spacer: {
    flex: 1,
  },
  forgottenPasswordLink: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  captcha: {
    marginBottom: theme.marginBase * 2,
  },
}));

const accountSchema = Yup.object().shape({
  email: Yup.string().email().required(),
  password: Yup.string().min(6).required(),
});

interface Props {
  defaultEmail?: string;
  onSignupClick?: () => any;
  onSuccess?: (account: Account) => any;
}

export const LoginContent = ({
  onSignupClick,
  onSuccess,
  defaultEmail,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const { mutateAsync: login } = useLogin();

  const initialValues = useMemo(
    () => ({
      email: defaultEmail || '',
      password: '',
      altcha: '',
    }),
    [defaultEmail],
  );

  const onSubmit = useCallback(
    async (
      values: typeof initialValues,
      helpers: FormikHelpers<typeof initialValues>,
    ) => {
      try {
        const account = await login(values);
        if (onSuccess) {
          onSuccess(account);
        }
      } catch (e) {
        helpers.setStatus(e);
        helpers.setSubmitting(false);
      }
    },
    [onSuccess, login],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={accountSchema}
      onSubmit={onSubmit}
    >
      <Form>
        <Input
          name="email"
          label={t('login.email')}
          placeholder="Ex: albert.einstein@gmail.com"
          type="email"
        />
        <Input
          name="password"
          label={t('login.password')}
          placeholder="****"
          type="password"
          rightAction={
            <Link
              to="/forgotten-password/request"
              className={classes.forgottenPasswordLink}
              target="_blank"
            >
              <Trans i18nKey="login.forgottenPassword" />
            </Link>
          }
        />

        <Captcha className={classes.captcha} invisible />

        <FormikError />

        <div className={classes.buttonsLine}>
          {onSignupClick && (
            <Button variant="transparent" onClick={onSignupClick}>
              <Trans i18nKey="login.signup" />
            </Button>
          )}
          <div className={classes.spacer} />
          <SubmitButton trackingName="Login" id="login_button">
            <Trans i18nKey="login.submit" />
          </SubmitButton>
        </div>
      </Form>
    </Formik>
  );
};
